/**
 * Date Formatting Utilities
 * 
 * PROJECT STANDARD: Use ISO 8601 YYYY-MM-DD for all calendar dates
 * - UI displays
 * - PDF exports
 * - Logs and timestamps
 * 
 * This ensures consistency, international compatibility, and proper sorting.
 */

/**
 * Format a date string or Date object to ISO 8601 format (YYYY-MM-DD)
 * @param dateString - ISO date string or Date object
 * @returns Formatted date string in YYYY-MM-DD format
 * @example
 * formatDateISO('2025-01-15T10:30:00Z') // '2025-01-15'
 * formatDateISO(new Date()) // '2025-01-15'
 */
export function formatDateISO(dateString: string | Date): string {
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  
  if (isNaN(date.getTime())) {
    return 'Invalid Date';
  }
  
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
}

/**
 * Format a date string to ISO 8601 with time (YYYY-MM-DD HH:MM:SS)
 * @param dateString - ISO date string or Date object
 * @returns Formatted datetime string
 * @example
 * formatDateTimeISO('2025-01-15T10:30:45Z') // '2025-01-15 10:30:45'
 */
export function formatDateTimeISO(dateString: string | Date): string {
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  
  if (isNaN(date.getTime())) {
    return 'Invalid Date';
  }
  
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

/**
 * Get today's date in ISO format for default form values
 * @returns Today's date in YYYY-MM-DD format
 */
export function getTodayISO(): string {
  return formatDateISO(new Date());
}

/**
 * Format file size for display
 * @param bytes - File size in bytes
 * @returns Formatted file size string
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}

